import React, { useState } from 'react';
import { ApolloError } from 'apollo-client';
import { FormFieldData, FormProvider } from 'modules/forms';
import { ObservationFileEntity } from 'shared/types/file/ObservationFileEntity';
import { useFiles } from 'shared/utils/files/hooks/useFiles';
import { allowedMimeTypes } from 'shared/options/observation-files.options';
import { mapFileToFormData } from 'shared/utils/mapping.utils';
import { useRestClient } from 'modules/rest-client/hooks/useRestClient';
import { IFileUploadResponse } from 'shared/types/file/IFileUploadRequest';
import { ErrorListToastMessage } from 'modules/errors';
import { useToasts } from 'react-toast-notifications';
import { useApolloClient } from '@apollo/react-hooks';
import { IAppConfig, useConfig } from 'modules/config';
import { IFormIsValidFunction } from 'modules/forms/types/IFormIsValidFunction';
import { ActionResponse } from 'modules/errors/classes/ActionResponse';
import Loading from 'components/layout/Loading';
import GraphQlErrorBoundary from 'components/layout/ErrorBoundary';
import { StyledNearMissContainer } from 'sections/near-miss-management/pages/ViewAmmendNearMiss/styled/ViewAmmendNearMiss.styled';
import { isNullOrUndefined } from 'shared/utils/validation.utils';
import { createIssuesOptions } from '../options/create-issues.options';
import { CreateIssueResponse, CreateIssueVariables } from '../mutations/create-issue.mutations.types';
import { CreateIssue } from '../mutations/create-issue.mutation';
import { CreateIssueRequestMapper } from '../mappers/CreateIssueMapper';
import NewIssueSubHeader from './IssueSubHeader';
import NewIssueForm from './NewIssueForm';
import NewIssueHeader from './NewIssueHeader';
import { IssueEntity } from 'shared/types/domain/IssueEntity';
import { NewIssueLoadingState } from '../enums/new-issue-loading-state.enum';

interface NewIssueMainProps {
   onComplete: () => void;
}

export const NewIssueMain = ({ onComplete }: NewIssueMainProps) => {
   const { addToast } = useToasts();
   const [issueForm, setIssueForm] = useState<FormFieldData<IssueEntity> | undefined>();
   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [loadingMessage, setLoadingMessage] = useState<string>(
      NewIssueLoadingState.loadingIssue
   );
   const [loadingError, setLoadingError] = useState<ApolloError>();

   const { settings } = useConfig<IAppConfig>();
   const restClient = useRestClient<'fileUpload'>();
   const setLoadingState = (loadingMessage: NewIssueLoadingState, isLoading: boolean): void => {
      setLoadingMessage(loadingMessage);
      setIsLoading(isLoading);
   };

   const client = useApolloClient();

   const { files, addFiles, clearFiles, syncFiles, removeFilesByIds } = useFiles<ObservationFileEntity>({
      allowedMimeTypes,
      onNotAllowed: handleNotAllowedFilesAdded,
      addFile: uploadFile,
      updateFile: () => { },
      removeFile: () => { },
      maxFileUploadBytes: settings.MaxFileUploadBytes
   });

   function handleNotAllowedFilesAdded(messages: string[]): void {
      addToast(
         <ErrorListToastMessage
            errors={messages}
            baseMessage={'There were disallowed file types in the collection of files uploaded'}
         />,
         { appearance: 'warning' }
      );
   }

   async function uploadFile(
      file: ObservationFileEntity,
      folderName?: string,
      data?: UID
   ): Promise<ObservationFileEntity> {
      const formData = mapFileToFormData<{ referenceId: UID | undefined }>(file, settings, folderName, {
         referenceId: data
      });

      const endpoint = restClient.getEndpoint('fileUpload');
      const result = await endpoint.post<IFileUploadResponse>({
         method: 'UploadFile',
         data: formData,
         onUploadProgress: p => { }
      });

      return { ...file, fileId: result.data.fileId };
   }

   const handleSaveChanges = async (
      formData: FormFieldData<IssueEntity> | undefined,
      fileData: ObservationFileEntity[] | undefined,
      isValid: IFormIsValidFunction
   ): Promise<ActionResponse> => {
      setLoadingState(NewIssueLoadingState.submitting, true);

      const response = await createEntity(formData, isValid);

      setLoadingState(NewIssueLoadingState.submitting, false);

      if (!response.successful) {
         addToast(<ErrorListToastMessage errors={[response.error.message]} />, { appearance: 'error' });

         return response;
      }

      if (files) {
         setLoadingState(NewIssueLoadingState.uploadingFiles, true);

         const typedData = response.data as CreateIssueResponse;
         const filesSynced = await syncFiles(typedData.observationId.toString(), typedData.uniqueRecordId);

         setLoadingState(NewIssueLoadingState.uploadingFiles, false);

         if (!filesSynced) {
            addToast(<ErrorListToastMessage errors={['There was an issue uploading files']} />, { appearance: 'error' });

            clearFiles();

            return response;
         }
      }

      addToast('Changes saved successfully', { appearance: 'success' });

      onComplete();

      return response;
   };

   const createEntity = async (
      formData: FormFieldData<IssueEntity> | undefined,
      isValid: IFormIsValidFunction
   ): Promise<any> => {
      let actionResponse = {};

      if (isNullOrUndefined(formData) || !isValid(true)) {
         return {
            successful: false,
            error: {
               message: 'Some of the information entered was not valid, please fix any validation errors before continuing'
            }
         };
      }

      const mapper = new CreateIssueRequestMapper();
      const requestObject = mapper.map(formData);

      try {
         const response = await client.mutate<{ createIssue: CreateIssueResponse }, CreateIssueVariables>({
            mutation: CreateIssue,
            variables: {
               request: requestObject
            }
         });

         actionResponse = { successful: true, data: response.data?.createIssue };

      } catch (error) {
         actionResponse = {
            successful: false,
            error: {
               message: "Issue Creating Issue",
               error: error
            }
         };
      }
      return actionResponse;
   }

   return (
      <React.Fragment>
         <Loading isLoading={isLoading} message={loadingMessage} noDelay>
            <GraphQlErrorBoundary error={loadingError}>
               <StyledNearMissContainer>
                  <FormProvider<IssueEntity> options={createIssuesOptions} formFields={issueForm} useEnglish>
                     <NewIssueHeader onSave={handleSaveChanges} isLoading={isLoading} files={files} />
                     <NewIssueSubHeader formHasErrors={false} goToOverview={() => { }} />
                     <NewIssueForm handleAddFile={addFiles} handleDeleteFile={removeFilesByIds} />
                  </FormProvider>
               </StyledNearMissContainer>
            </GraphQlErrorBoundary>
         </Loading>
      </React.Fragment>
   );
};
